import React, { useCallback, useEffect, useState } from 'react'
import { DatePicker, Drawer, message } from 'antd'
import Select from 'react-select'
import { useSelector } from 'react-redux'
import { Link, Route, Switch, useHistory, useParams } from 'react-router-dom'

import { useToggle, usePet, stringifyDate, ws, useSocketEvents } from '../../modules'

import Receive from '../reusable/receive'
import { EntryFields, Icd11ConceptPicker } from '../reusable/inputs'
import SendFor from '../reusable/sendFor'

import PatientDetails from './patient'
import Medication from './medication'

const   Consultation = ({ path, onClose }) => {
    const [entry, setEntry] = useState()
    const [primaryDiagnosis, setPrimaryDiagnosis] = useState(null)
    const [secondaryDiagnoses, setSecondaryDiagnoses] = useState([])
    const [diagnosisDirty, setDiagnosisDirty] = useState(false)
    const [completeAfterDiagnosisSave, setCompleteAfterDiagnosisSave] = useState(false)
    const [followUpOpen, setFollowUpOpen] = useState(false)
    const [followUpType, setFollowUpType] = useState(null)
    const [followUpDate, setFollowUpDate] = useState(null)
    const [followUpNotes, setFollowUpNotes] = useState('')
    const [groups, setGroups] = useState([])
    const [tab, setTab] = useState("1")
    const [screening, setScreening] = useState(null) // linked outreach screening (if referred)

    const history = useHistory()
    const { id } = useParams()

    const sendCurrent = useCallback(() => {
        ws.send('currently-engaging', [entry?.ref?.patient?._id, localStorage.getItem('station')])
    }, [entry?.ref?.patient?._id])

    useEffect(() => sendCurrent(), [sendCurrent])

    useSocketEvents({
        'engaged-patients': {
            callback: sendCurrent
        }
    })

    const { user } = useSelector(state => state.application)

    let canEdit = user?.issuperuser || !entry?.filled || (entry.filledBy?._id === user?._id)

    const [visible, toggleVisible] = useToggle(true)

    const [{ loading, error }] = usePet('itemGroups', null, { notify: true, callback: ({ data }) => setGroups(data) })
    const [consultationTypes, setConsultationTypes] = useState([])
    const [{ loading: ctLoading }] = usePet('consultationTypes', { string: '', page: 1, limit: 100 }, { notify: true, callback: ({ data }) => setConsultationTypes(data?.consultationTypes ?? []) })

    const [{ loading: eLoading, error: eError }, getEntry] = usePet('singleEntry', { id }, { notify: true, callback: ({ data }) => setEntry(data) })

    // If this patient came in through an outreach referral, pull the linked screening
    // so the clinician sees the CHW's VA/findings/triage above the exam form (§3.1 item 3).
    const [, fetchScreening] = usePet('outreachScreenings', { patient: entry?.ref?.patient?._id, limit: 1 }, {
        notify: true,
        manual: true,
        callback: ({ data }) => setScreening(data?.screenings?.[0] ?? null),
    })

    useEffect(() => {
        if (entry?.ref?.patient?._id) fetchScreening({ patient: entry.ref.patient._id, limit: 1 })
        else setScreening(null)
    }, [entry?.ref?.patient?._id])


    useEffect(() => {
        const diagnoses = entry?.diagnoses ?? []
        const primary = diagnoses.find(diagnosis => diagnosis?.role === 'primary') ?? diagnoses[0] ?? null
        const secondary = diagnoses.filter(diagnosis => diagnosis?.role !== 'primary')

        setPrimaryDiagnosis(primary)
        setSecondaryDiagnoses(secondary)
        setDiagnosisDirty(false)
    }, [entry])

    const reload = () => {
        setEntry()
        getEntry()
    }

    const close = () => {
        toggleVisible()
        setTimeout(() => history.goBack(), 300)
    }
    
    const [{ loading: compLoading }, completeEntry] = usePet('completeEntry', { id: entry?._id }, { notify: true, callback: (_, notif, ops) => {
        notif('Changes have been saved', ops('success'))
        onClose()
        close()
    }, manual: true, load: { msg: 'Saving changes...' } })

    const [{ loading: savingDiagnoses }, saveDiagnoses] = usePet('saveConsultationDiagnoses', {
        entryId: entry?._id,
        diagnoses: [
            ...(primaryDiagnosis ? [primaryDiagnosis] : []),
            ...secondaryDiagnoses,
        ],
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Saving diagnoses...' },
        callback: ({ data }, notif, ops) => {
            setEntry(data)
            setDiagnosisDirty(false)
            notif('Diagnoses saved', ops('success'))

            if (completeAfterDiagnosisSave) {
                setCompleteAfterDiagnosisSave(false)
                completeEntry()
            }
        },
    })

    const [{ loading: schedulingFollowUp }, scheduleFollowUp] = usePet('scheduleConsultationFollowUp', {
        entryId: entry?._id,
        consultationType: followUpType?.value,
        scheduledFor: followUpDate?._d ?? followUpDate?.toDate?.() ?? followUpDate,
        notes: followUpNotes,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Scheduling follow up...' },
        callback: (_, notif, ops) => {
            notif('Follow up scheduled', ops('success'))
            setFollowUpOpen(false)
            setFollowUpType(null)
            setFollowUpDate(null)
            setFollowUpNotes('')
            reload()
        },
    })

    const setNewTab = () => setTab("0")

    const persistDiagnoses = ({ complete = false } = {}) => {
        if (!primaryDiagnosis) {
            message.warning('Please select a primary diagnosis before continuing')
            return false
        }

        setCompleteAfterDiagnosisSave(complete)
        saveDiagnoses()
        return true
    }

    const completeConsultation = () => {
        if (diagnosisDirty) {
            const startedSave = persistDiagnoses({ complete: true })
            if (!startedSave) return
            return
        }

        completeEntry()
    }

    return (
        <Drawer
            closable
            maskClosable
            visible={visible}
            onClose={close}
            footer={
                <div className="w-full text-right">
                    {
                        canEdit &&
                        <button onClick={ completeConsultation } disabled={ compLoading || savingDiagnoses || schedulingFollowUp || (diagnosisDirty && !primaryDiagnosis) } className="text-white mr-2 hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">{ entry?.filled ? 'Update' : 'Complete' }</button>
                    }
                    <Link to={`${path}/${id}/${entry?.ref?.patient?._id}`}>
                        <button className="text-white mr-2 hisani-btn-green font-medium rounded-lg text-sm px-5 py-2.5 text-center">Patient Details</button>
                    </Link>
                    <button onClick={ close } className="text-white mr-2 bg-gray-500 hover:bg-gray-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Close</button>
                </div>
            }
            width="60%">
            <p className="font-extrabold text-3xl mb-0">Review Patient</p>
            <Receive
                progress
                loading={eLoading}
                error={eError}>
                {
                    entry ?
                        <>
                            <p className="text-gray-500 mb-2 text-xs italic">{entry?.ref?.patient?.name ?? 'Patient'}'s visit {entry?.date && `on ${stringifyDate(entry.date)}`} -  { entry?.filledBy && `Seen By ${ entry?.filledBy?.name } on ${ stringifyDate(entry.filledOn) }` }</p>
                            { screening && (
                                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                                    <div className="text-xs font-semibold text-blue-900 uppercase tracking-wide mb-1">Outreach screening · triage {screening.triage?.level}</div>
                                    <div className="text-sm text-gray-700">
                                        {screening.demographics?.name} — VA R {screening.va?.od || '—'} / L {screening.va?.os || '—'}
                                        {screening.triage?.reason && <> · {screening.triage.reason}</>}
                                    </div>
                                    {screening.checklist?.cornea && <div className="text-xs text-gray-600 mt-1">Cornea: {screening.checklist.cornea}</div>}
                                    {screening.checklist?.pupil && <div className="text-xs text-gray-600">Pupil: {screening.checklist.pupil}</div>}
                                    {screening.checklist?.other && <div className="text-xs text-gray-600">Other: {screening.checklist.other}</div>}
                                </div>
                            )}
                            {
                                <EntryFields
                                    reload={ reload }
                                    entry={entry}
                                    fields={entry?.item?.fields ?? []} />
                            }
                            <div className="px-3 mt-6">
                                <p className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Diagnosis</p>
                                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-4">
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                        Primary Diagnosis
                                    </label>
                                    <Icd11ConceptPicker
                                        toolId={ `consultation_primary_${id}` }
                                        disabled={ !canEdit }
                                        value={ primaryDiagnosis }
                                        onChange={ value => {
                                            setPrimaryDiagnosis(value)
                                            setDiagnosisDirty(true)
                                        } } />
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
                                        Secondary Diagnoses
                                    </label>
                                    <Icd11ConceptPicker
                                        multi
                                        toolId={ `consultation_secondary_${id}` }
                                        disabled={ !canEdit }
                                        value={ secondaryDiagnoses }
                                        onChange={ values => {
                                            setSecondaryDiagnoses(values.filter(item => item?.code !== primaryDiagnosis?.code))
                                            setDiagnosisDirty(true)
                                        } } />
                                    <div className="flex items-center justify-between mt-3">
                                        <p className="text-xs text-gray-500 mb-0">A primary diagnosis is required before this consultation can be completed.</p>
                                        {
                                            canEdit &&
                                            <button
                                                onClick={ () => persistDiagnoses() }
                                                disabled={ !primaryDiagnosis || !diagnosisDirty || savingDiagnoses }
                                                className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-4 py-2 text-center">
                                                Save Diagnoses
                                            </button>
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="px-3 mt-2">
                                <p className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">Follow Up</p>
                                <div className="bg-green-50 border border-green-100 rounded-lg p-4 mb-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-700 mb-1">Schedule Follow-up</p>
                                            <p className="text-xs text-gray-500 mb-0">Create a dedicated appointment record linked to this consultation.</p>
                                        </div>
                                        {
                                            canEdit &&
                                            <button onClick={ () => setFollowUpOpen(!followUpOpen) } className="text-white bg-green-600 hover:bg-green-700 font-medium rounded-lg text-sm px-4 py-2 text-center">
                                                { followUpOpen ? 'Close' : 'Schedule Follow-up' }
                                            </button>
                                        }
                                    </div>
                                    {
                                        followUpOpen &&
                                        <div className="mt-4 border-t border-green-100 pt-4">
                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                        Consultation Type
                                                    </label>
                                                    <Select
                                                        isLoading={ ctLoading }
                                                        value={ followUpType }
                                                        onChange={ setFollowUpType }
                                                        options={ consultationTypes.map(item => ({ label: item?.name, value: item?._id })) }
                                                        placeholder="Select follow-up type" />
                                                </div>
                                                <div>
                                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                        Follow-up Date
                                                    </label>
                                                    <DatePicker
                                                        showTime
                                                        className="w-full"
                                                        value={ followUpDate }
                                                        onChange={ setFollowUpDate } />
                                                </div>
                                            </div>
                                            <div className="mt-4">
                                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                                    Notes
                                                </label>
                                                <textarea
                                                    rows="3"
                                                    value={ followUpNotes }
                                                    onChange={ event => setFollowUpNotes(event.target.value) }
                                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4"
                                                    placeholder="Optional follow-up instructions" />
                                            </div>
                                            <div className="text-right mt-4">
                                                <button
                                                    onClick={ () => scheduleFollowUp() }
                                                    disabled={ !followUpType || !followUpDate || schedulingFollowUp }
                                                    className="text-white bg-green-600 hover:bg-green-700 font-medium rounded-lg text-sm px-4 py-2 text-center">
                                                    Save Follow-up
                                                </button>
                                            </div>
                                        </div>
                                    }
                                    <div className="mt-4">
                                        {
                                            (entry?.followUpAppointments ?? []).length > 0 ?
                                                entry.followUpAppointments.map(followUp =>
                                                    <div key={ followUp?._id } className="border border-green-100 rounded-lg bg-white p-3 mb-2">
                                                        <p className="font-semibold text-sm mb-1">{ followUp?.consultationType?.name ?? 'Follow Up' }</p>
                                                        <p className="text-xs text-gray-500 mb-1">Scheduled for { stringifyDate(followUp?.scheduledFor, true) }</p>
                                                        { followUp?.notes && <p className="text-xs text-gray-600 mb-1">{ followUp.notes }</p> }
                                                        <p className="text-xs text-gray-500 mb-0">Status: { followUp?.status ?? 'Scheduled' }</p>
                                                    </div>)
                                                :
                                                <p className="text-xs text-gray-500 mb-0 mt-3">No dedicated follow-up appointments have been scheduled yet.</p>
                                        }
                                    </div>
                                </div>
                            </div>
                            <p className="block uppercase px-3 tracking-wide mt-3 text-gray-700 text-xs font-bold mb-2" htmlFor="grid-name">ACTIONS</p>
                            <div className="flex px-3 md:mb-0">
                                <Link to={`${path}/${id}/medication`}>
                                    <button onClick={setNewTab} className="text-white mr-2 hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">Medication</button>
                                </Link>
                                <Link to={`${path}/${id}/treatment`}>
                                    <button onClick={setNewTab} className="text-white mr-2 bg-green-500 hover:bg-green-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Treatment</button>
                                </Link>
                                <Link to={`${path}/${id}/investigations`}>
                                    <button onClick={setNewTab} className="text-white mr-2 bg-red-500 hover:bg-red-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center">Investigations</button>
                                </Link>
                            </div>
                            <Switch>
                                <Route path={ `${path}/:id/investigations` }>
                                    <SendFor
                                        source={`${path}/${id}`}
                                        attachedTo={entry}
                                        {...{ loading, error, groups, tab }}
                                        group="Investigations"
                                        patient={entry?.ref?.patient} />
                                </Route>
                                <Route path={ `${path}/:id/treatment` }>
                                    <SendFor
                                        source={`${path}/${id}`}
                                        attachedTo={entry?.ref?._id}
                                        {...{ loading, error, groups, tab }}
                                        group="Treatment"
                                        patient={entry?.ref?.patient} />
                                </Route>
                                <Route path={ `${path}/:id/medication` }>
                                    <Medication
                                        source={`${path}/${id}`}
                                        attachedTo={entry?.ref?._id}
                                        {...{ loading, error, groups, tab }}
                                        patient={entry?.ref?.patient} />
                                </Route>
                                <Route path={ `${path}/:id/:pat` }>
                                    <PatientDetails attachedTo={entry?.ref?._id} reload={ reload } source={ `${path}/${id}` } />
                                </Route>
                            </Switch>
                        </>
                        : null
                }
            </Receive>
        </Drawer>
    )
}

export default Consultation
